import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalDialog } from '../../../../components/ModalDialog';
import { useAgreements } from '../../../../hooks/api/useAgreements';
import { masterlistCheckQueryKey } from '../../../../hooks/api/useMasterlistCheck';
import { Agreements } from '../../../../types/Agreements.types';
import { StyledInputLabel } from '../../../../components/inputs/InputLabel';
import { AccessibleSelect } from '../../../../components/inputs/AccessibleSelect';

export const MoveJournalModal = ({
  journalId,
  agreementId,
  journalUrl,
  onSuccess,
  disabled,
}: {
  journalId: string;
  agreementId: string;
  journalUrl: string;
  onSuccess: () => void;
  disabled?: boolean;
}) => {
  const { fetchAgreements, moveJournal } = useAgreements();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [agreements, setAgreements] = useState<Agreements[]>([]);
  const [targetAgreementId, setTargetAgreementId] = useState('');
  const [url, setUrl] = useState(journalUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [movedToAgreementName, setMovedToAgreementName] = useState('');
  const [error, setError] = useState<string | undefined>();

  const handleOpen = async () => {
    setOpen(true);
    setTargetAgreementId('');
    setUrl(journalUrl);
    setIsLoading(false);
    setIsSuccess(false);
    setMovedToAgreementName('');
    setError(undefined);
    try {
      const all = await fetchAgreements();
      setAgreements((all ?? []).filter((a) => a._id !== agreementId));
    } catch {
      setError('Failed to load agreements.');
    }
  };

  // Both lists changed. Not on success: this table would refetch the check while still listing
  // the moved journal. The source is only marked stale: we're leaving it, so a refetch now would
  // be thrown away.
  const goToMovedJournal = () => {
    queryClient.invalidateQueries({
      queryKey: masterlistCheckQueryKey(agreementId),
      refetchType: 'none',
    });
    queryClient.invalidateQueries({
      queryKey: masterlistCheckQueryKey(targetAgreementId),
    });
    navigate(
      `/agreements/${targetAgreementId}?journal=${journalId}&source=move`
    );
    onSuccess();
  };

  const handleClose = () => {
    if (isLoading) return;
    // After a move, X, Escape and a click outside do what the dialog says will happen, like Ok.
    if (isSuccess) {
      goToMovedJournal();
      return;
    }
    setOpen(false);
  };

  const handleMove = async () => {
    setError(undefined);
    setIsLoading(true);
    try {
      const targetName =
        agreements.find((a) => a._id === targetAgreementId)?.name ?? '';
      await moveJournal(agreementId, journalId, targetAgreementId, url);
      setMovedToAgreementName(targetName);
      setIsSuccess(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button variant="link" onClick={handleOpen} disabled={disabled}>
        Move
      </Button>
      <ModalDialog
        open={open}
        onClose={handleClose}
        title="Move journal to another agreement"
        error={error}
        maxWidth="sm"
        actions={
          isSuccess ? (
            <Button
              variant="primary"
              onClick={goToMovedJournal}
            >
              Ok
            </Button>
          ) : (
            <>
              <Button
                variant="default"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleMove}
                disabled={isLoading || !targetAgreementId || !url.trim()}
              >
                {isLoading && (
                  <CircularProgress
                    size={20}
                    sx={{ color: 'white', marginRight: 1 }}
                  />
                )}
                {isLoading ? 'Moving…' : 'Move'}
              </Button>
            </>
          )
        }
      >
        {isSuccess ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography>
              Journal moved to agreement{' '}
              <strong>&quot;{movedToAgreementName}&quot;</strong>.
            </Typography>
            <Typography>
              You will now be redirected to the journal edit page in the target
              agreement. Remember to change any other field that needs
              adjusting.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography variant="body2" sx={{ mb: 8 }}>
              After moving you will be redirected to the edit journal page of
              the target agreement. Remember to change any other field that
              needs adjusting.
            </Typography>
            <Box>
              <AccessibleSelect
                label="To agreement"
                fullWidth
                id="move-target-agreement"
                value={targetAgreementId}
                onChange={(e) => setTargetAgreementId(e.target.value)}
                disabled={isLoading}
              >
                {agreements.map((a) => (
                  <MenuItem key={a._id} value={a._id}>
                    {a.name}
                  </MenuItem>
                ))}
              </AccessibleSelect>
            </Box>
            <Box>
              <StyledInputLabel htmlFor="move-journal-url">
                URL
              </StyledInputLabel>
              <TextField
                fullWidth
                id="move-journal-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={isLoading}
                required
              />
            </Box>
          </Box>
        )}
      </ModalDialog>
    </>
  );
};
